import { useState, useMemo } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Lock, ArrowLeft, CheckCircle, Eye, EyeSlash } from '@phosphor-icons/react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatApiErrorDetail(detail) {
  if (detail == null) return 'Something went wrong. Please try again.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map(e => e?.msg || JSON.stringify(e)).filter(Boolean).join(' ');
  if (detail?.msg) return detail.msg;
  return String(detail);
}

export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = useMemo(() => params.get('token') || '', [params]);

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  const tooShort = password.length > 0 && password.length < 8;
  const mismatch = confirm.length > 0 && confirm !== password;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!token) { setError('Reset link is missing or malformed. Request a new one.'); return; }
    if (password.length < 8) { setError('Password must be at least 8 characters'); return; }
    if (password !== confirm) { setError('Passwords do not match'); return; }
    setError('');
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, new_password: password });
      setDone(true);
      toast.success('Password updated. You can sign in with your new password.');
      setTimeout(() => navigate('/login'), 1800);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(formatApiErrorDetail(detail) || 'Could not reset your password. Try requesting a new link.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-amber-50 via-white to-blue-50/30 px-4 relative overflow-hidden">
      <div className="absolute top-1/4 -left-32 w-96 h-96 bg-amber-100/40 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-1/4 -right-32 w-96 h-96 bg-blue-100/30 rounded-full blur-3xl pointer-events-none" />
      <div className="w-full max-w-md relative z-10">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-white border border-amber-200 shadow-sm mb-4">
            <Lock size={26} className="text-blue-700" weight="duotone" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 tracking-tight" style={{ fontFamily: "'Libre Baskerville', serif" }}>
            {done ? 'Password updated' : 'Set a new password'}
          </h1>
          <p className="text-gray-600 mt-2 text-sm">
            {done ? 'Redirecting you to sign in…' : 'Choose a strong password — at least 8 characters.'}
          </p>
        </div>
        <div className="bg-white/80 backdrop-blur border border-gray-100 rounded-2xl p-8 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
          {done ? (
            <div className="flex flex-col items-center text-center gap-4" data-testid="reset-password-done">
              <CheckCircle size={48} className="text-emerald-600" weight="fill" />
              <p className="text-sm text-gray-700">You can sign in with your new password now.</p>
              <Link to="/login" className="text-sm text-blue-700 hover:underline">Go to sign in</Link>
            </div>
          ) : !token ? (
            <div className="p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-700" data-testid="reset-password-no-token">
              This reset link is missing. <Link to="/forgot-password" className="font-semibold underline">Request a new one</Link>.
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-5" data-testid="reset-password-form">
              {error && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-700" data-testid="reset-password-error">{error}</div>
              )}
              <div className="space-y-2">
                <Label htmlFor="password">New password</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPwd ? 'text' : 'password'}
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    placeholder="At least 8 characters"
                    required
                    data-testid="reset-password-new-input"
                    className="h-11 pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPwd(s => !s)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    data-testid="reset-password-show-toggle"
                    aria-label={showPwd ? 'Hide password' : 'Show password'}
                  >
                    {showPwd ? <EyeSlash size={16} /> : <Eye size={16} />}
                  </button>
                </div>
                {tooShort && <p className="text-[11px] text-amber-700">Password must be at least 8 characters</p>}
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirm">Confirm new password</Label>
                <Input
                  id="confirm"
                  type={showPwd ? 'text' : 'password'}
                  value={confirm}
                  onChange={e => setConfirm(e.target.value)}
                  placeholder="Repeat the password"
                  required
                  data-testid="reset-password-confirm-input"
                  className="h-11"
                />
                {mismatch && <p className="text-[11px] text-rose-700">Passwords do not match</p>}
              </div>
              <Button type="submit" disabled={loading || password.length < 8 || password !== confirm} className="w-full bg-blue-700 hover:bg-blue-800 text-white h-11 font-semibold" data-testid="reset-password-submit-btn">
                {loading ? 'Updating…' : 'Update password'}
              </Button>
            </form>
          )}
        </div>
        <p className="text-center text-xs text-gray-400 mt-6">
          <Link to="/login" className="inline-flex items-center gap-1 text-gray-600 hover:text-blue-700" data-testid="reset-password-back-to-login">
            <ArrowLeft size={12} /> Back to sign in
          </Link>
        </p>
      </div>
    </div>
  );
}
